import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { API_URL } from '../../utils';

interface UserProfileData {
  email: string;
  birthDate: string | null;
  height: number | null;
  gender: string | null;
  language: string;
  goals: string | null;
  notes: string | null;
}

interface UserProfileProps {
  onServiceUnavailable?: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ onServiceUnavailable }) => {
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { fetchWithAuth } = useAuth();
  const { t, setLanguage } = useLanguage();

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/user/profile`);
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      const data = await response.json();
      console.log('Profile data from backend:', data);
      console.log('Birth date type:', typeof data.birth_date);
      console.log('Birth date value:', data.birth_date);
      console.log('Raw profile:', data);
      const parsedDate = data.birth_date ? new Date(data.birth_date) : null;
      console.log('Parsed date:', parsedDate);
      setProfile({
        email: data.email,
        birthDate: data.birth_date,
        height: data.height,
        gender: data.gender,
        language: data.language,
        goals: data.goals,
        notes: data.notes
      });
      setLanguage(data.language);
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('profile.error'));
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t, onServiceUnavailable, setLanguage]);

  useEffect(() => {
    if (profile === null) {
      fetchProfile();
    }
  }, [fetchProfile, profile]);

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetchWithAuth(`${API_URL}/user/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birthDate: profile.birthDate,
          height: profile.height,
          gender: profile.gender,
          language: profile.language,
          goals: profile.goals,
          notes: profile.notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('profile.error'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return <Typography color="error">{t('profile.error')}</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('profile.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('profile.success')}
          </Alert>
        )}

        <Stack spacing={3}>
          <TextField
            label={t('profile.email')}
            value={profile.email}
            disabled
            fullWidth
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('profile.birthDate')} 
              value={profile.birthDate ? new Date(profile.birthDate) : null}
              onChange={(date) => {
                console.log('Selected date:', date);
                const formattedDate = date ? date.toISOString().split('T')[0] : null;
                console.log('Formatted date:', formattedDate);
                setProfile(prev => prev ? { ...prev, birthDate: formattedDate } : null);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: false,
                  helperText: profile.birthDate || ''
                }
              }}
            />
          </LocalizationProvider>

          <TextField
            label={t('profile.height')}
            type="number"
            value={profile.height ?? ''}
            onChange={(e) => setProfile({ ...profile, height: e.target.value ? parseFloat(e.target.value) : null })}
            fullWidth
          />

          <TextField
            select
            label={t('profile.gender')}
            value={profile.gender ?? ''}
            onChange={(e) => setProfile({ ...profile, gender: e.target.value || null })}
            fullWidth
          >
            <MenuItem value="">{t('profile.gender.other')}</MenuItem>
            <MenuItem value="male">{t('profile.gender.male')}</MenuItem>
            <MenuItem value="female">{t('profile.gender.female')}</MenuItem>
            <MenuItem value="other">{t('profile.gender.other')}</MenuItem>
          </TextField>

          <TextField
            select
            label={t('profile.language')}
            value={profile.language}
            onChange={(e) => setProfile({ ...profile, language: e.target.value })}
            fullWidth
          >
            <MenuItem value="en">English</MenuItem>
            <MenuItem value="es">Español</MenuItem>
          </TextField>

          <TextField
            label={t('profile.goals')}
            multiline
            rows={4}
            value={profile.goals ?? ''}
            onChange={(e) => setProfile({ ...profile, goals: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label={t('profile.notes')}
            multiline
            rows={4}
            value={profile.notes ?? ''}
            onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            fullWidth
          >
            {saving ? t('profile.saving') : t('profile.save')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}; 